/**
 * The "Sign in with Google" button.
 *
 * Renders nothing at all when the deployment has no client id. That is the whole feature flag:
 * an organization that has not set one sees the password form exactly as before, rather than a
 * button that fails when pressed.
 *
 * Google Identity Services draws the button itself, inside an iframe it controls, so the visual
 * result is not ours to style beyond width and theme. That is deliberate on Google's part — a
 * button that looks like Google's but is not is how consent dialogs get spoofed — and worth
 * accepting rather than working around.
 */

import { useEffect, useId, useRef } from 'react'
import { useI18n } from '../lib/i18n'

/** The shape of the global that the Google script installs. */
interface GoogleIdentityServices {
  accounts: {
    id: {
      initialize: (config: {
        client_id: string
        callback: (response: { credential?: string }) => void
        auto_select?: boolean
        cancel_on_tap_outside?: boolean
      }) => void
      renderButton: (parent: HTMLElement, options: Record<string, string | number>) => void
    }
  }
}

declare global {
  interface Window {
    google?: GoogleIdentityServices
  }
}

const SCRIPT_URL = 'https://accounts.google.com/gsi/client'
const SCRIPT_ID = 'devpulse-google-identity'

/** Loads the Google script once per page, and resolves when it is ready to use. */
function loadGoogleScript(): Promise<void> {
  if (typeof document === 'undefined') return Promise.reject(new Error('no document'))
  if (window.google?.accounts?.id) return Promise.resolve()

  const existing = document.getElementById(SCRIPT_ID) as HTMLScriptElement | null
  if (existing) {
    return new Promise((resolve, reject) => {
      existing.addEventListener('load', () => resolve())
      existing.addEventListener('error', () => reject(new Error('google script failed')))
    })
  }

  return new Promise((resolve, reject) => {
    const script = document.createElement('script')
    script.id = SCRIPT_ID
    script.src = SCRIPT_URL
    script.async = true
    script.defer = true
    script.onload = () => resolve()
    script.onerror = () => reject(new Error('google script failed'))
    document.head.appendChild(script)
  })
}

export function GoogleSignIn({
  clientId,
  onCredential,
  disabled,
}: {
  /** Null when the deployment has not configured Google. Nothing renders. */
  clientId: string | null
  onCredential: (idToken: string) => void
  disabled?: boolean
}) {
  const { locale } = useI18n()
  const container = useRef<HTMLDivElement>(null)
  const id = useId()

  // Held in a ref so the callback Google keeps hold of always calls the current handler, rather
  // than the one that existed when the button was drawn.
  const handler = useRef(onCredential)
  handler.current = onCredential

  useEffect(() => {
    if (!clientId || !container.current) return

    let cancelled = false

    loadGoogleScript()
      .then(() => {
        if (cancelled || !container.current || !window.google) return

        window.google.accounts.id.initialize({
          client_id: clientId,
          callback: (response) => {
            if (response.credential) handler.current(response.credential)
          },
          // No automatic sign-in and no one-tap prompt. Signing somebody in because a browser
          // remembered them is a decision they did not make on this visit.
          auto_select: false,
          cancel_on_tap_outside: true,
        })

        window.google.accounts.id.renderButton(container.current, {
          type: 'standard',
          theme: 'outline',
          size: 'large',
          text: 'signin_with',
          shape: 'rectangular',
          logo_alignment: 'left',
          width: 320,
          locale,
        })
      })
      .catch(() => {
        // A blocked or unreachable script leaves the password form untouched. Google being
        // unavailable is not a reason to make the page look broken.
      })

    return () => {
      cancelled = true
    }
  }, [clientId, locale])

  if (!clientId) return null

  return (
    <div className="stack stack--tight">
      <div className="auth__divider" aria-hidden="true" />
      <div ref={container} id={id} style={{ opacity: disabled ? 0.5 : 1, pointerEvents: disabled ? 'none' : 'auto' }} />
    </div>
  )
}
